import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Shield,
  MapPin,
  Users,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import BusImage from "../components/BusImage";
import FeatureCard from "../components/FeatureCard";
import {
  LiveTrackingCard,
  SafetyStatusCard,
  ParentAlertCard,
  AttendanceCard,
} from "../components/StatusCards";

const features = [
  {
    icon: Shield,
    title: "Safety First",
    description: "Child safety is our top priority",
  },
  {
    icon: MapPin,
    title: "Live GPS Tracking",
    description: "Real-time Tracking for complete peace of mind",
  },
  {
    icon: Users,
    title: "Trained Staff",
    description: "Verified, trained and background checked",
  },
  {
    icon: CheckCircle2,
    title: "Transparent Operations",
    description: "Technology driven visibility for parents and schools",
  },
];

const Home = () => {
  return (
    <PageWrapper testId="home-page">
      {/* HERO */}
      <section className="hero-panel-bg pt-6 sm:pt-10 pb-10">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-8">
          {/* Left copy */}
          <div className="lg:col-span-5 relative z-10 lg:pt-10">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="brand-headline text-4xl sm:text-5xl lg:text-[3.5rem] mb-6"
              data-testid="hero-title"
            >
              Safe School Transport.
              <br />
              <span className="brand-blue">Professionally Managed</span>
              <span className="text-amber-500">.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="text-slate-600 text-base leading-relaxed max-w-[480px] mb-8"
              data-testid="hero-description"
            >
              We are the company who professionally runs your school bus
              operations, and we use technology to make it safe and
              transparent.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Link
                to="/services"
                data-testid="hero-cta-button"
                className="group inline-flex items-center gap-3 px-6 py-3 rounded-xl border-2 border-[#2563EB] text-[#2563EB] font-semibold hover:bg-[#2563EB] hover:text-white transition-colors duration-300"
              >
                Our Services
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
            </motion.div>
          </div>

          {/* Middle: bus + cards */}
          <div className="lg:col-span-7 relative xl:min-h-[760px]">
            <div className="relative">
              <BusImage variant="home" />
            </div>
          
            {/* Tablet/mobile: cards beneath */}
            <div className="xl:hidden mt-6 grid sm:grid-cols-2 gap-4 justify-items-center">
              <LiveTrackingCard />
              <SafetyStatusCard />
              <ParentAlertCard />
              <AttendanceCard />
            </div>
          </div>
        </div>
      </section>

      {/* Feature cards row */}
      <section
        className="max-w-[1280px] mx-auto px-5 lg:px-10 mb-14 mt-4"
        data-testid="home-features"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:max-w-[680px]">
          {features.map((f, i) => (
            <FeatureCard
              key={f.title}
              icon={f.icon}
              title={f.title}
              description={f.description}
              variant="icon-bg"
              index={i}
              testId={`home-feature-${i}`}
            />
          ))}
        </div>
      </section>
    </PageWrapper>
  );
};

export default Home;
